/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  Card,
  Container,
  Label,
  Page,
  TextInput,
  Typography,
  useAlertContext,
} from '@bw/alloy-react';
import { HookshotError } from '@bw/hookshot';
import { useNavigate } from 'react-router';
import ExpressHeader from 'components/ExpressHeader';
import { useSendTNVerificationCode } from 'hooks/express/useSendTNVerificationCode';
import { useSendTNVerification } from 'hooks/express/useSendTNVerification';
import { useSearchParams } from 'react-router-dom';
import config from 'utils/config';

const VERIFICATION_CODE_LENGTH = 6;

// The registration service returns 409 when the number is already verified and
// the account fully provisioned
const isAlreadyProvisioned = (e: unknown): boolean =>
  e instanceof HookshotError && e.response?.status === 409;

const TNVerification: React.FC = () => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('token');
  const tn = searchParams.get('phoneNumber');
  const email = searchParams.get('email');
  const sendTNVerificationCode = useSendTNVerificationCode();
  const sendTNVerification = useSendTNVerification();
  const [values, setValues] = useState<Array<string>>(
    Array(VERIFICATION_CODE_LENGTH).fill(''),
  );
  const { alertDanger, alertSuccess } = useAlertContext();
  const [loading, setLoading] = useState(false);

  const redirectToApp = useCallback(() => {
    if (config.dashboard) {
      window.location.replace(config.dashboard);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (!tn || !registrationId) {
      navigate('/signup');
      return;
    }

    const fetchData = async () => {
      try {
        await sendTNVerification({
          input: { phoneNumber: tn, email },
        });
      } catch (e) {
        if (isAlreadyProvisioned(e)) {
          navigate('/login');
          return;
        }
        alertDanger(
          e?.data || 'We were unable to message the provided phone number.',
        );
      }
    };

    fetchData();
  }, [
    tn,
    registrationId,
    email,
    navigate,
    alertSuccess,
    alertDanger,
    sendTNVerification,
  ]);

  useEffect(() => {
    alertSuccess('Thanks for verifying your email address');
  }, [alertSuccess]);

  const focusInput = (index: number) => {
    const el = inputsRef.current[index];
    if (!el) return;
    el.focus();
    el.select();
  };

  const handleChange = (rawValue: string, index: number) => {
    let cursor = index;
    const newValues = [...values];
    const characters = rawValue.trim().split('');

    characters.forEach((char) => {
      if (cursor < VERIFICATION_CODE_LENGTH) {
        newValues[cursor] = char;
        cursor++;
      }
    });

    setValues(newValues);
    const nextIndex = Math.min(cursor, VERIFICATION_CODE_LENGTH - 1);
    focusInput(nextIndex);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newValues = [...values];

      if (newValues[index]) {
        newValues[index] = '';
        setValues(newValues);
        return;
      }

      if (index > 0) {
        newValues[index - 1] = '';
        setValues(newValues);
        focusInput(index - 1);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault(); // Avoid ALSO triggering a 'change' event
    const pasted = e.clipboardData.getData('text').trim();
    handleChange(pasted, 0);
  };

  const verify = async () => {
    const code = values.join('');
    try {
      setLoading(true);
      await sendTNVerificationCode({
        input: {
          email,
          phoneNumber: tn,
          code,
        },
      });

      navigate(`/new-password?token=${registrationId}`, {
        state: { registrationId },
      });
    } catch (e) {
      if (isAlreadyProvisioned(e)) {
        redirectToApp();
        return;
      }
      setLoading(false);
      alertDanger({
        message: (
          <>
            That code doesn&apos;t match.{' '}
            <Button textOnly onClick={sendNewCode}>
              Try a new one
            </Button>
          </>
        ),
      });
    }
  };

  const sendNewCode = async () => {
    try {
      setLoading(true);
      await sendTNVerification({
        input: { phoneNumber: tn, email },
      });
      alertSuccess(`We sent a confirmation code to ${tn}`);
    } catch (e) {
      alertDanger(e?.data || 'Resend failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const allFilled = values.every((v) => v !== '');

  return (
    <Page
      className="bg-transparent"
      documentMeta={[{ name: 'robots', content: 'noindex, nofollow' }]}
    >
      <Container
        breakpoint="md"
        className="d-flex flex-column flex-grow gap-5 flex-grow-1 my-4 px-4"
      >
        <ExpressHeader />
        <Card className="p-4 p-sm-5">
          <div className="d-flex flex-column gap-2">
            <Typography variant="h2" data-bw="verify-phone-heading">
              Verify your phone number
            </Typography>
            <Typography tag="div" variant="help-text" data-bw="code-sent-text">
              Check your texts for a verification code. Didn&apos;t get it?{' '}
              <Button textOnly onClick={sendNewCode} data-bw="send-new-code">
                Send a new code
              </Button>
            </Typography>
          </div>
          <div className="d-flex flex-column gap-2">
            <Label required data-bw="code-sent-label">
              Code sent to {tn}
            </Label>
            <section className="d-flex gap-2">
              {values.map((val, i) => (
                <TextInput
                  key={i}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  ref={(el) => {
                    inputsRef.current[i] =
                      el as unknown as HTMLInputElement | null;
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 50,
                    fontSize: '1rem' /* Avoid zoom on mobile */,
                  }}
                  onChange={(value) => handleChange(value as string, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  autoComplete={i === 0 ? 'one-time-code' : undefined}
                  inputClassName="text-center"
                  value={val}
                  data-bw={`verification-code-input-${i}`}
                />
              ))}
            </section>
          </div>

          <div className="d-flex mt-4 gap-4">
            <Button
              loading={loading}
              disabled={!allFilled}
              onClick={verify}
              data-bw="verify-code"
            >
              Verify
            </Button>
          </div>
        </Card>
      </Container>
    </Page>
  );
};

export default TNVerification;
